import { translate } from 'shared/lib/i18n.tsx';

import {
  createFruitSliceGame,
  type FruitSliceGame,
  type HudState,
  type SoundEvent,
} from '../games/fruit-slice-web/fruitSliceWebGame.ts';
import { parseFruitSliceWebFrame, type FruitSliceWebFrame } from '../games/fruit-slice-web/fruitSliceWebFrame.ts';
import type { FruitMode, Players } from '../games/fruit-slice-web/fruitSliceRules.ts';
import {
  resetFruitNinjaRemoteUi,
  setFruitNinjaRemoteUiSnapshot,
} from '../games/fruit-ninja-remote/state/fruitNinjaRemoteUiStore.ts';

/**
 * Fruit Slice Web на ТВ (GEN-18338), игра комнаты `fruit-slice-web`. Как «Полёт птицы»: игра
 * (`fruitSliceWebGame`, та же, что в приложении) живёт здесь, телефон — камера и контроллер: шлёт
 * `{type: 'FruitSliceWebData', gameState, mode, players, detectionProgress, hands, active, t?}` ~30 раз/с.
 * HUD — тот же, что у нативного Fruit Slice на ТВ (`FruitNinjaRemoteTvOverlays`: счёт, жизни, серия, таймер,
 * комбо, пауза, итог с победителем на двоих): движок кладёт в его стор состояние из игры.
 * Счёт и итог ТВ отдаёт телефону через `onScore` / `onFinished` → `game:state`.
 */
export type FruitSliceWebPhase = 'lobby' | 'calibration' | 'playing' | 'paused' | 'gameover';

const MEDIA = '/media';
const FRUITS = ['apple', 'kiwi', 'peach', 'strawberry', 'watermelon'] as const;

const SOUNDS = {
  spawn: ['fly_out_1.mp3', 'fly_out_2.mp3'],
  cut: ['cut_1.mp3', 'cut_2.mp3', 'cut_3.mp3'],
  blast: ['bomb_blast.mp3'],
  hit: ['hit.mp3'],
  fuse: ['bomb_fuse.mp3'],
} as const;

const MODE_NAMES: Record<FruitMode, 'CLASSIC' | 'ARCADE' | 'ZEN'> = { classic: 'CLASSIC', arcade: 'ARCADE', zen: 'ZEN' };

export class FruitSliceWebDisplayEngine {
  private phase: FruitSliceWebPhase = 'lobby';

  private host: HTMLDivElement | null = null;

  private calibCanvas: HTMLCanvasElement | null = null;

  private calibRaf = 0;

  private game: FruitSliceGame | null = null;

  private progress: [number, number] = [0, 0];

  private mode: FruitMode = 'classic';

  private players: Players = 2;

  private hud: HudState | null = null;

  /** Фитили бомб по id — гасим, когда бомба разрезана или улетела. */
  private readonly fuses = new Map<number, HTMLAudioElement>();

  onScore?: (scores: [number, number]) => void;

  onFinished?: (scores: [number, number]) => void;

  getPhase(): FruitSliceWebPhase {
    return this.phase;
  }

  /** Комната выбрала игру: экран калибровки, ждём кадры телефона. */
  startCalibration(): void {
    this.ensureGame().reset();
    this.stopFuses();
    this.phase = 'calibration';
    this.progress = [0, 0];
    this.showCalibration(true);
    resetFruitNinjaRemoteUi();
  }

  /** `start-game` с телефона; сама партия стартует по кадру `STARTED` — там режим и число игроков. */
  startGame(): void {
    this.ensureGame();
  }

  onGameFrame(raw: unknown): void {
    const frame = parseFruitSliceWebFrame(raw);
    if (frame) this.apply(frame);
  }

  reset(): void {
    this.phase = 'lobby';
    this.stopFuses();
    cancelAnimationFrame(this.calibRaf);
    this.calibRaf = 0;
    this.game?.destroy();
    this.game = null;
    this.host?.remove();
    this.host = null;
    this.calibCanvas = null;
    this.hud = null;
  }

  private apply(frame: FruitSliceWebFrame): void {
    const game = this.ensureGame();
    this.mode = frame.mode;
    this.players = frame.players;
    switch (frame.gameState) {
      case 'PREPARING':
        // Калибровка на телефоне, в том числе после «Начать заново».
        if (this.phase !== 'calibration') {
          this.stopFuses();
          game.reset();
          resetFruitNinjaRemoteUi();
          this.phase = 'calibration';
          this.showCalibration(true);
        }
        this.progress = frame.detectionProgress;
        break;
      case 'STARTED':
        // После итога новая партия — только через PREPARING (рестарт с калибровкой на телефоне): запоздавший
        // STARTED не должен молча сбросить счёт и начать игру поверх экрана итога.
        if (this.phase === 'calibration' || this.phase === 'lobby') {
          this.showCalibration(false);
          game.setMode(frame.mode, frame.players);
          game.start();
          this.phase = 'playing';
        } else if (this.phase === 'paused') {
          game.resume();
          this.resumeFuses();
          this.phase = 'playing';
          this.publishHud();
        }
        break;
      case 'PAUSED':
        if (this.phase === 'playing') {
          game.pause();
          this.pauseFuses();
          this.phase = 'paused';
          this.publishHud();
        }
        break;
      default:
        break;
    }
    if (this.phase === 'playing') game.setHands({ hands: frame.hands, active: frame.active, t: frame.t });
  }

  private ensureGame(): FruitSliceGame {
    if (this.game) return this.game;
    const host = document.createElement('div');
    // Под HUD Fruit Slice (его z-index — 10 000+), над общей канвой и лобби.
    host.style.cssText = `position:fixed;inset:0;z-index:1;background:#1a0f08 url(${MEDIA}/fruitSliceBg.jpg) center/cover;`;
    document.body.appendChild(host);
    const calib = document.createElement('canvas');
    calib.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;z-index:2;display:none;';
    host.appendChild(calib);
    this.host = host;
    this.calibCanvas = calib;

    const fruit = (name: string) => ({ whole: `${MEDIA}/${name}_whole.png`, half: `${MEDIA}/${name}_half.png` });
    const images = Object.fromEntries(FRUITS.map((name) => [name.toUpperCase(), fruit(name)]));
    this.game = createFruitSliceGame({
      host,
      pixelRatio: 1,
      images: { ...images, BOMB: { whole: `${MEDIA}/bomb.png` } } as Parameters<typeof createFruitSliceGame>[0]['images'],
      strings: { combo: translate('fruit.combo', { n: '{n}' }), score: translate('fruit.score') },
      drawHud: false,
      drawFlash: true,
      onHud: (state) => {
        this.hud = state;
        this.publishHud();
      },
      onScore: (scores) => this.onScore?.(scores),
      onFinished: (scores) => {
        this.phase = 'gameover';
        this.stopFuses();
        this.publishHud();
        this.onFinished?.(scores);
      },
      onSound: (event) => this.playSound(event),
    });
    return this.game;
  }

  /** Состояние игры → стор HUD нативного Fruit Slice на ТВ. Итог на двоих он сам показывает с победителем. */
  private publishHud(): void {
    const hud = this.hud;
    if (!hud) return;
    const [p1, p2] = hud.players;
    const split = this.players === 2;
    const hasLives = this.mode === 'classic';
    const score = split ? Math.max(p1?.score ?? 0, p2?.score ?? 0) : p1?.score ?? 0;
    const timeLeft = hud.timeLeft ?? (this.mode === 'zen' ? hud.elapsed : null);
    setFruitNinjaRemoteUiSnapshot({
      hudVisible: this.phase === 'playing',
      pauseVisible: this.phase === 'paused',
      gameOverVisible: this.phase === 'gameover',
      score,
      pauseScore: score,
      gameOverScore: score,
      lives: hasLives ? p1?.lives ?? null : null,
      timeLeftSec: timeLeft,
      timerUrgent: hud.timeLeft !== null && hud.timeLeft <= 10,
      gameMode: MODE_NAMES[this.mode],
      streak: p1?.streak ?? 0,
      comboCount: p1?.combo?.count ?? null,
      comboNormX: p1?.combo?.x ?? null,
      comboNormY: p1?.combo?.y ?? null,
      combo2Count: p2?.combo?.count ?? null,
      combo2NormX: p2?.combo?.x ?? null,
      combo2NormY: p2?.combo?.y ?? null,
      bestScore: null,
      split,
      player1Score: split ? p1?.score ?? 0 : null,
      player1Lives: split && hasLives ? p1?.lives ?? null : null,
      player1Streak: split ? p1?.streak ?? 0 : null,
      player2Score: split ? p2?.score ?? 0 : null,
      player2Lives: split && hasLives ? p2?.lives ?? null : null,
      player2Streak: split ? p2?.streak ?? 0 : null,
      player1GameOver: split && (p1?.eliminated ?? false),
      player2GameOver: split && (p2?.eliminated ?? false),
    });
  }

  // ─── Звук ─────────────────────────────────────────────────────────────────

  private playSound(event: SoundEvent): void {
    if (event.name === 'fuseStop') {
      this.fuses.get(event.id)?.pause();
      this.fuses.delete(event.id);
      return;
    }
    const files = SOUNDS[event.name];
    const audio = new Audio(`${MEDIA}/${files[Math.floor(Math.random() * files.length)]}`);
    if (event.name === 'fuse') {
      audio.loop = true;
      this.fuses.set(event.id, audio);
    }
    // Автозапуск звука на ТВ может быть запрещён до первого нажатия — тогда игра просто без звука.
    audio.play().catch(() => undefined);
  }

  private pauseFuses(): void {
    this.fuses.forEach((audio) => audio.pause());
  }

  private resumeFuses(): void {
    this.fuses.forEach((audio) => { audio.play().catch(() => undefined); });
  }

  private stopFuses(): void {
    this.fuses.forEach((audio) => audio.pause());
    this.fuses.clear();
  }

  // ─── Калибровка ───────────────────────────────────────────────────────────

  private showCalibration(visible: boolean): void {
    cancelAnimationFrame(this.calibRaf);
    this.calibRaf = 0;
    if (!this.calibCanvas) return;
    this.calibCanvas.style.display = visible ? 'block' : 'none';
    if (!visible) return;
    const tick = (): void => {
      if (this.phase !== 'calibration') return;
      this.drawCalibration();
      this.calibRaf = requestAnimationFrame(tick);
    };
    tick();
  }

  /** Рамки игроков, как на телефоне: соло — одна по центру, на двоих — по одной на половину. */
  private drawCalibration(): void {
    const canvas = this.calibCanvas;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const W = window.innerWidth;
    const H = window.innerHeight;
    if (canvas.width !== W) canvas.width = W;
    if (canvas.height !== H) canvas.height = H;
    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = 'rgba(0,0,0,0.55)';
    ctx.fillRect(0, 0, W, H);
    const boxW = W * 0.3;
    const boxH = H * 0.7;
    const top = H * 0.17;
    const centers = this.players === 1 ? [0.5] : [0.25, 0.75];
    centers.forEach((center, index) => {
      const x = W * center - boxW / 2;
      ctx.lineWidth = 6;
      ctx.strokeStyle = 'rgba(255,255,255,0.8)';
      ctx.setLineDash([18, 12]);
      ctx.strokeRect(x, top, boxW, boxH);
      ctx.setLineDash([]);
      const progress = this.progress[index];
      if (progress > 0) {
        ctx.strokeStyle = '#4cd964';
        ctx.lineWidth = 10;
        ctx.beginPath();
        ctx.moveTo(x, top + boxH);
        ctx.lineTo(x, top + boxH - boxH * Math.min(1, progress));
        ctx.moveTo(x + boxW, top + boxH);
        ctx.lineTo(x + boxW, top + boxH - boxH * Math.min(1, progress));
        ctx.stroke();
      }
    });
    ctx.fillStyle = '#ffe566';
    ctx.font = `700 ${Math.round(H * 0.045)}px -apple-system, system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(translate('calib.hint.upper_body'), W / 2, top / 2);
  }
}
